import React, {useEffect, useRef, useState} from "react";
import {useIntl, FormattedMessage} from "react-intl";
import {
    X,
    Check,
    HelpCircle,
    AlertTriangle,
    ShieldAlert,
    Loader2,
    Ticket,
    Clock,
    ChevronRight,
    ChevronDown,
    Bell,
    BellOff
} from "lucide-react";
import {getThemeColor} from "../../utils/topicUtils";
import {getContrastColors} from "../../UIElements";
import SessionMarker from "./SessionMarker";
import FeedbackTag from "./FeedbackTag";
import SupportRequestPill, {supportRequestState} from "./SupportRequestPill";
import {getSessionOutcome, getStopPointLabel, isFeedbackOnly, sessionKind, sessionHasFeedback, sessionDate} from "./sessionUtils";

const SENTIMENT_KEYS = {
    "-2": {id: "analysis.sentiment.veryNegative", def: "Very negative"},
    "-1": {id: "analysis.sentiment.negative", def: "Negative"},
    "0": {id: "analysis.sentiment.neutral", def: "Neutral"},
    "1": {id: "analysis.sentiment.positive", def: "Positive"},
    "2": {id: "analysis.sentiment.veryPositive", def: "Very positive"},
}

function outcomeLabel(status, intl) {
    switch (status) {
        case "success": return intl.formatMessage({id: "analysis.successful", defaultMessage: "Successful"})
        case "failed": return intl.formatMessage({id: "analysis.failed", defaultMessage: "Failed"})
        case "uncertain": return intl.formatMessage({id: "analysis.uncertain", defaultMessage: "Uncertain"})
        case "blocked": return intl.formatMessage({id: "analysis.blocked", defaultMessage: "Blocked"})
        case "feedback": return intl.formatMessage({id: "analysis.feedbackSession", defaultMessage: "Feedback"})
        default: return intl.formatMessage({id: "analysis.notAnalyzed", defaultMessage: "Not analyzed"})
    }
}

const OUTCOME_ICON = {
    success: <Check size={14} />,
    failed: <X size={14} />,
    uncertain: <HelpCircle size={14} />,
    blocked: <ShieldAlert size={14} />,
    unanalyzed: <AlertTriangle size={14} />,
}

function fullDateTime(s, intl) {
    const d = new Date(sessionDate(s))
    return `${intl.formatDate(d, {day: "numeric", month: "short", year: "numeric"})}, ${intl.formatTime(d, {hour: "2-digit", minute: "2-digit"})}`
}

function shortDateTime(s, intl) {
    const d = new Date(sessionDate(s))
    return `${intl.formatDate(d, {day: "numeric", month: "short"})} ${intl.formatTime(d, {hour: "2-digit", minute: "2-digit"})}`
}

function campaignNameOf(session, surveyNames, intl) {
    return session.survey
        ? (surveyNames?.[session.survey] || session.survey)
        : intl.formatMessage({id: "analysis.directMessage", defaultMessage: "Direct message"})
}

function messageCountOf(session) {
    return Array.isArray(session.messages) ? session.messages.length : 0
}

function SentimentDisplay({start, end}) {
    const intl = useIntl()
    if (start == null && end == null) return null
    const fmt = (val) => {
        if (val == null) return "?"
        const k = SENTIMENT_KEYS[String(val)]
        return k ? intl.formatMessage({id: k.id, defaultMessage: k.def}) : val
    }
    return <div className="sentiment-display">{fmt(start)} → {fmt(end)}</div>
}

function FeedbackSection({response, details}) {
    const intl = useIntl()
    return <div className={`feedback-section ${response || "none"}`}>
        <div className="feedback-label">
            <FormattedMessage id="analysis.studentFeedback" defaultMessage="Feedback on earlier conversation:" />
        </div>
        <div className={`feedback-response ${response || "none"}`}>
            <FeedbackTag response={response} />
            {response === "positive" && intl.formatMessage({id: "analysis.feedback.positive", defaultMessage: "Positive"})}
            {response === "negative" && intl.formatMessage({id: "analysis.feedback.negative", defaultMessage: "Negative"})}
        </div>
        {details && <div className="feedback-details-text">{intl.formatMessage({id: "analysis.feedbackDetailsQuoted", defaultMessage: '"{details}"'}, {details})}</div>}
    </div>
}

function TopicPills({codes, topicLabels, topicThemes}) {
    if (!codes || codes.length === 0) return null
    return <div className="session-topics">
        {codes.map(code => {
            const color = getThemeColor(topicThemes?.[code])
            return <span
                key={code}
                className="topic-tag"
                style={{backgroundColor: color, color: getContrastColors(color).textColor}}
            >
                {topicLabels?.[code] || code}
            </span>
        })}
    </div>
}

// The single layout atom of the panel: an uppercase header with its content
// always beneath it. Everything in the hero body is a Field so labels and
// spacing read consistently. Renders nothing when there's no content.
function Field({label, children}) {
    if (!children) return null
    return <div className="panel-field">
        {label && <div className="field-head">{label}</div>}
        {children && <div className="field-body">{children}</div>}
    </div>
}

// A descriptive reasoning (the bot's rationale) in a tinted callout, so it reads
// distinctly from plain values. Renders nothing without text.
function Reasoning({className, children}) {
    if (!children) return null
    return <div className={`reasoning ${className && className}`}>{children}</div>
}

// One step of the support funnel: a labelled yes/no state. `onLabel`/`offLabel`
// replace the default "Yes"/"No" text (e.g. the escalation target "category → role"
// on the created cell, or "Not created" on the drop-off). `onIcon`/`offIcon` override
// the default check/cross (the created cell uses the bell / bell-off so it matches the
// support-request pills). `tone` overrides the default yes→on / no→off color (used to
// flag the suggested-but-not-created drop-off as a warning).
function FunnelCell({label, on, tone, onLabel, offLabel, onIcon, offIcon}) {
    const intl = useIntl()
    const cls = tone || (on ? "on" : "off")
    return <div className="funnel-cell">
        <div className="funnel-cell-label">{label}</div>
        <div className={`funnel-state ${cls}`}>
            {on ? (onIcon || <Check size={14} />) : (offIcon || <X size={14} />)}
            {on
                ? (onLabel || intl.formatMessage({id: "analysis.yes", defaultMessage: "Yes"}))
                : (offLabel || intl.formatMessage({id: "analysis.no", defaultMessage: "No"}))}
        </div>
    </div>
}


function SupportFunnel({analysis, supportNeed, droppedOff}) {
    const intl = useIntl()
    const created = !!supportNeed
    const category = supportNeed?.category
        ? intl.formatMessage({id: `topic.${supportNeed.category}`, defaultMessage: supportNeed.category})
        : null
    const role = supportNeed?.supporttype
        ? intl.formatMessage({id: `role.${supportNeed.supporttype}`, defaultMessage: supportNeed.supporttype})
        : null
    const target = created && (category || role)
        ? `${category || ""}${category && role ? " → " : ""}${role || ""}`
        : null

    return <div className="support-funnel">
        <FunnelCell
            label={intl.formatMessage({id: "analysis.supportGiven", defaultMessage: "Support given"})}
            on={!!analysis.support_given}
        />
        <FunnelCell
            label={intl.formatMessage({id: "analysis.supportRequestSuggested", defaultMessage: "Support request suggested"})}
            on={!!analysis.support_request_suggested}
        />
        <FunnelCell
            label={intl.formatMessage({id: "analysis.supportRequestCreated", defaultMessage: "Support request created"})}
            on={created}
            tone={droppedOff ? "warn" : null}
            onLabel={target}
            onIcon={<Bell size={14} />}
            offIcon={droppedOff ? <BellOff size={14} /> : undefined}
            offLabel={droppedOff ? intl.formatMessage({id: "analysis.funnel.notCreated", defaultMessage: "Not created"}) : undefined}
        />
    </div>
}

function HeroSession({session, contactId, surveyNames, topicLabels, topicThemes, supportNeeds, needAttribution, onRequestAnalysis, pendingAnalysis}) {
    const intl = useIntl()
    const analysis = session.llmanalysis
    const {status} = getSessionOutcome(session)
    const blocked = status === "blocked"
    const campaignName = campaignNameOf(session, surveyNames, intl)
    const messageCount = messageCountOf(session)
    const hasFeedback = sessionHasFeedback(session) || isFeedbackOnly(session)
    const supportState = supportRequestState(session, contactId, needAttribution, supportNeeds)
    const supportNeed = supportState.need

    const kind = sessionKind(session)
    const feedbackOnly = kind === "feedback_only"
    const feedbackThenSupport = kind === "feedback_then_support"
    const [showAllAnalysis, setShowAllAnalysis] = useState(false)

    const language = analysis?.language
        ? (intl.formatDisplayName?.(analysis.language, {type: "language"}) || analysis.language)
        : null

    const conversationFields = <>
        <Field>
            {analysis?.summary}
        </Field>
        {analysis?.topics?.length > 0 && (
            <Field>
                <TopicPills codes={analysis.topics} topicLabels={topicLabels} topicThemes={topicThemes} />
            </Field>
        )}
        <Field>
            <SupportFunnel analysis={analysis} supportNeed={supportNeed} droppedOff={supportState.kind === "suggested"} />
        </Field>

        {analysis?.provided_support && (
            <Field>
                <Reasoning className={"less-margin"}>{analysis.provided_support}</Reasoning>
            </Field>
        )}

        <Field label={intl.formatMessage({id: "analysis.referrals", defaultMessage: "Referrals & Resources"})}>
            {analysis?.referrals?.length > 0 ? (
                <ul className="referral-list">
                    {analysis.referrals.map((r, i) => <li key={i}>{r}</li>)}
                </ul>
            ) : (
                <span className="field-placeholder">
                    <FormattedMessage id="analysis.noReferrals" defaultMessage="No referrals made" />
                </span>
            )}
        </Field>

        {getStopPointLabel(analysis?.conversation_stop_point, intl) && (
            <Field label={intl.formatMessage({id: "analysis.ending", defaultMessage: "Ending"})}>
                {getStopPointLabel(analysis.conversation_stop_point, intl)}
                <Reasoning>{analysis.conversation_stop_point_detail}</Reasoning>
            </Field>
        )}

        {(analysis?.user_sentiment_start != null || analysis?.user_sentiment_end != null) && (
            <Field label={intl.formatMessage({id: "analysis.sentiment", defaultMessage: "Message Tone"})}>
                <SentimentDisplay start={analysis.user_sentiment_start} end={analysis.user_sentiment_end} />
            </Field>
        )}

        <Field label={intl.formatMessage({id: "analysis.language", defaultMessage: "Language"})}>
            {language}
        </Field>

        {analysis?.success_evaluation_reason && (
            <Field label={intl.formatMessage({id:"analysis.stats.botPerformance", defaultMessage: "Bot Performance"})}>
                {status !== "feedback" && <span className={`outcome-label ${status}`}>{OUTCOME_ICON[status] || OUTCOME_ICON.unanalyzed}{outcomeLabel(status, intl)}</span>}
                <Reasoning>{analysis.success_evaluation_reason}</Reasoning>
            </Field>
        )}
    </>

    return <div className="hero-session">
        <div className="hero-head">
            {analysis && !blocked && status !== "feedback" && <span className={`outcome-label ${status}`}>{outcomeLabel(status, intl)}</span>}
            {!analysis && <span className={"outcome-label"}>{outcomeLabel(null, intl)}</span>}
            <span className="hero-date">{fullDateTime(session, intl)}</span>
        </div>
        <div className="hero-sub">
            {campaignName}
            {messageCount > 0 && <> · <FormattedMessage id="analysis.totalMessageCount" defaultMessage="{count, plural, one {# message} other {# messages}}" values={{count: messageCount}} /></>}
        </div>

        {!analysis && (
            <div className="hero-empty">
                {onRequestAnalysis && (
                    <button
                        className={`request-analysis-btn ${pendingAnalysis ? "pending" : ""}`}
                        onClick={() => !pendingAnalysis && onRequestAnalysis(session.id)}
                        disabled={pendingAnalysis}
                    >
                        {pendingAnalysis
                            ? <><Loader2 size={12} className="spin" /> <FormattedMessage id="analysis.analyzing" defaultMessage="Analyzing..." /></>
                            : <FormattedMessage id="analysis.requestAnalysis" defaultMessage="Request analysis" />}
                    </button>
                )}
            </div>
        )}

        {blocked && (
            <div className="hero-blocked">
                <ShieldAlert size={14} />
                <div>
                    <FormattedMessage id="analysis.blocked" defaultMessage="Blocked" />
                    {analysis.blockReason && <div className="block-reason">{analysis.blockReason}</div>}
                </div>
            </div>
        )}

        {analysis && !blocked && (
            <div className="hero-fields">
                {(feedbackOnly || feedbackThenSupport) && (
                    <FeedbackSection response={analysis.feedback_response ?? null} details={analysis.feedback_details} />
                )}

                {feedbackThenSupport && (
                    <div className="analysis-scope-note">
                        <FormattedMessage
                            id="analysis.feedbackThenSupportScope"
                            defaultMessage="The analysis below covers the feedback and the conversation that followed it."
                        />
                    </div>
                )}

                {feedbackOnly ? (
                    <>
                        <button
                            type="button"
                            className="analysis-collapse-toggle"
                            onClick={() => setShowAllAnalysis(v => !v)}
                            aria-expanded={showAllAnalysis}
                        >
                            {showAllAnalysis ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                            {showAllAnalysis ?
                                <FormattedMessage id="analysis.hideAnalysisForFeedback" defaultMessage="Hide analysis of feedback" /> :
                                <FormattedMessage id="analysis.showAnalysisForFeedback" defaultMessage="Show analysis of feedback" />
                            }
                        </button>
                        {showAllAnalysis && conversationFields}
                    </>
                ) : conversationFields}
            </div>
        )}
    </div>
}

function CompactSession({session, contactId, surveyNames, supportNeeds, needAttribution, onFocusSession}) {
    const intl = useIntl()
    const analysis = session.llmanalysis
    const {status} = getSessionOutcome(session)
    const feedbackOnly = isFeedbackOnly(session)
    const campaignName = campaignNameOf(session, surveyNames, intl)
    const messageCount = messageCountOf(session)
    const hasFeedback = sessionHasFeedback(session)

    const snippet = feedbackOnly ? analysis?.feedback_details : analysis?.summary

    return <div className="compact-session" onClick={() => onFocusSession(session.id)}>
        <div className={"cs-header"}>
            {status !== "feedback" && <span className={`outcome-label ${status}`}>{outcomeLabel(status, intl)}</span>}
            {(feedbackOnly || hasFeedback) && <FeedbackTag response={analysis?.feedback_response ?? null} />}
            <SupportRequestPill session={session} contactId={contactId} attribution={needAttribution} supportNeeds={supportNeeds} />
        </div>
        <div className="cs-line">
            <span className="cs-meta">
                {campaignName} · {shortDateTime(session, intl)}
                {messageCount > 0 && <> · <FormattedMessage id="analysis.totalMessageCount" defaultMessage="{count, plural, one {# message} other {# messages}}" values={{count: messageCount}} /></>}
            </span>
        </div>
        {snippet && <div className="cs-summary">{snippet}</div>}
    </div>
}

export default function SessionDetailPanel({
    contact,
    focusSessionId,
    onClose,
    onFocusSession,
    surveyNames,
    topicLabels,
    topicThemes,
    supportNeeds,
    needAttribution,
    onRequestAnalysis,
    pendingAnalysisSessions,
}) {
    const intl = useIntl()
    const focusedRef = useRef(null)

    useEffect(() => {
        if (focusedRef.current) {
            focusedRef.current.scrollIntoView({block: "start"})
        }
    }, [focusSessionId, contact?.contactId])

    if (!contact) return null

    const {contactName, contactId, sessions} = contact
    // Oldest first: a thread reads top-to-bottom; the auto-scroll handles long lists.
    const ordered = [...sessions].sort((a, b) => sessionDate(a).localeCompare(sessionDate(b)))

    const unanalyzed = sessions.filter(s => !s.llmanalysis)
    const hasUnanalyzed = unanalyzed.length > 0
    // "Request all" is in flight while any unanalyzed session is still pending.
    const allPending = hasUnanalyzed && unanalyzed.every(s => pendingAnalysisSessions?.has(s.id))

    const first = ordered[0]
    const last = ordered[ordered.length - 1]
    const dateRange = ordered.length === 1
        ? intl.formatDate(new Date(sessionDate(first)), {day: "numeric", month: "short", year: "numeric"})
        : `${intl.formatDate(new Date(sessionDate(first)), {day: "numeric", month: "short"})} – ${intl.formatDate(new Date(sessionDate(last)), {day: "numeric", month: "short", year: "numeric"})}`

    return <div className="detail-panel">
        <div className="panel-header">
            <div>
                <h2>{contactName}</h2>
                <div className="panel-meta">
                    <FormattedMessage id="analysis.conversationCount" defaultMessage="{count, plural, one {# conversation} other {# conversations}}" values={{count: ordered.length}} />
                    {" · "}{dateRange}
                </div>
            </div>
            <button className="close-btn" onClick={onClose}><X size={18} /></button>
        </div>

        <div className="thread-timeline">
            {ordered.map((session, i) => {
                const isFocused = session.id === focusSessionId
                const {status, feedback} = getSessionOutcome(session)
                const gap = i > 0 ? gapLabel(ordered[i - 1], session, intl) : null

                return <React.Fragment key={session.id}>
                    {gap && (
                        <div className="tl-gap">
                            <div className="tl-rail">
                                <span className="tl-gap-label"><Clock size={12} /> {gap}</span>
                            </div>
                        </div>
                    )}
                    <div className={`tl-entry ${isFocused ? "focused" : ""}`} ref={isFocused ? focusedRef : null}>
                        <div className="tl-rail">
                            <span className="tl-node">
                                <SessionMarker status={status} feedback={feedback} />
                            </span>
                        </div>
                        <div className="tl-body">
                            {isFocused ? (
                                <HeroSession
                                    session={session}
                                    contactId={contactId}
                                    surveyNames={surveyNames}
                                    topicLabels={topicLabels}
                                    topicThemes={topicThemes}
                                    supportNeeds={supportNeeds}
                                    needAttribution={needAttribution}
                                    onRequestAnalysis={onRequestAnalysis}
                                    pendingAnalysis={pendingAnalysisSessions?.has(session.id)}
                                />
                            ) : (
                                <CompactSession
                                    session={session}
                                    contactId={contactId}
                                    surveyNames={surveyNames}
                                    supportNeeds={supportNeeds}
                                    needAttribution={needAttribution}
                                    onFocusSession={onFocusSession}
                                />
                            )}
                        </div>
                    </div>
                </React.Fragment>
            })}
        </div>

        {hasUnanalyzed && (
            <div className="panel-footer">
                <button
                    className={`request-analysis-btn ${allPending ? "pending" : ""}`}
                    onClick={() => !allPending && unanalyzed.forEach(s => onRequestAnalysis?.(s.id))}
                    disabled={allPending}
                >
                    {allPending
                        ? <><Loader2 size={14} className="spin" /> <FormattedMessage id="analysis.analyzing" defaultMessage="Analyzing..." /></>
                        : <FormattedMessage id="analysis.requestAnalysisForAll" defaultMessage="Request analysis for all" />}
                </button>
            </div>
        )}
    </div>
}

function gapLabel(prev, next, intl) {
    const ms = Math.abs(new Date(sessionDate(next)) - new Date(sessionDate(prev)))
    const mins = Math.round(ms / 60000)
    if (mins < 1) return intl.formatMessage({id: "analysis.gap.lessThanMinute", defaultMessage: "< 1 min"})
    if (mins < 60) return intl.formatMessage({id: "analysis.gap.minutes", defaultMessage: "{n} min"}, {n: mins})
    const hours = Math.round(mins / 60)
    if (hours < 24) return intl.formatMessage({id: "analysis.gap.hours", defaultMessage: "{n} h"}, {n: hours})
    const days = Math.round(hours / 24)
    return intl.formatMessage({id: "analysis.gap.days", defaultMessage: "{n} d"}, {n: days})
}
